import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { shouldRejectClientSideToken } from '@/helpers/jwtClient';
import { isValidMxUserPayload, userPayloadFromMeResponseBody } from '@/helpers/userPayload';
import { store as vuexStore } from '@/integrations/vuex';
import { authApi } from '@/services/api';

export interface MxUser {
  id: number | string;
  name?: string;
  email?: string;
  [key: string]: unknown;
}

const STORAGE_KEY_TOKEN = 'token';
const STORAGE_KEY_USER = 'userData';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null);
  const userData = ref<MxUser | null>(null);
  let sessionValidation: Promise<boolean> | null = null;

  const isAuthenticated = computed(() => token.value !== null && userData.value !== null);

  function restoreTokenFromStorage (): boolean {
    if (token.value) {
      return true;
    }

    const saved = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (saved === null) {
      return false;
    }

    try {
      const parsed = JSON.parse(saved) as string | null;
      if (!parsed || shouldRejectClientSideToken(parsed)) {
        localStorage.removeItem(STORAGE_KEY_TOKEN);
        return false;
      }

      token.value = parsed;
      vuexStore.commit('updateData', { name: 'token', data: parsed });
      return true;
    } catch {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      return false;
    }
  }

  function restoreUserFromStorage (): boolean {
    if (userData.value) {
      return true;
    }

    const saved = localStorage.getItem(STORAGE_KEY_USER);
    if (saved === null) {
      return false;
    }

    try {
      const user = JSON.parse(saved) as MxUser;
      userData.value = user;
      vuexStore.commit('updateData', { name: 'userData', data: user });
      return true;
    } catch {
      localStorage.removeItem(STORAGE_KEY_USER);
      return false;
    }
  }

  function restoreSessionFromStorage (): boolean {
    if (!restoreTokenFromStorage()) {
      clearAuth();
      return false;
    }

    restoreUserFromStorage();
    return true;
  }

  function setToken (newToken: string): void {
    token.value = newToken;
    sessionValidation = null;
    localStorage.setItem(STORAGE_KEY_TOKEN, JSON.stringify(newToken));
    vuexStore.commit('updateData', { name: 'token', data: newToken, save: true });
  }

  function setUser (user: MxUser): void {
    userData.value = user;
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    vuexStore.commit('updateData', { name: 'userData', data: user, save: true });
  }

  function clearAuth (): void {
    token.value = null;
    userData.value = null;
    sessionValidation = null;
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER);
    vuexStore.commit('clearData', 'userData');
    vuexStore.commit('clearData', 'token');
  }

  async function validateSession (): Promise<boolean> {
    try {
      const response = await authApi.getMe({ skipUnauthorizedHandler: true });
      const payload = userPayloadFromMeResponseBody(response.data);

      if (isValidMxUserPayload(payload)) {
        setUser(payload as MxUser);
        return true;
      }

      clearAuth();
      return false;
    } catch {
      clearAuth();
      return false;
    }
  }

  async function ensureAuthenticatedSession (): Promise<boolean> {
    if (!token.value) {
      restoreSessionFromStorage();
    }

    if (!token.value) {
      return false;
    }

    sessionValidation ??= validateSession();
    return sessionValidation;
  }

  async function logout (): Promise<void> {
    try {
      await authApi.invalidateToken();
    } catch {
      // Best-effort: proceed with client-side cleanup even if server call fails
    }
    const logoutUrl = authApi.getSsoLogoutUrl(`${window.location.origin}/logout`);
    clearAuth();
    window.location.href = logoutUrl;
  }

  return {
    token,
    userData,
    isAuthenticated,
    setToken,
    setUser,
    clearAuth,
    ensureAuthenticatedSession,
    logout,
  };
});
